import CustomDataGridResult from '../../../../../../utils/Datagrid/CustomForecastDataGridResult';
import { useCallback, useState } from 'react';
import { parcelSynthesisTheme } from '../../../../../../utils/Datagrid/CustomForecastDataGridUtils';
import { checkObjectsEquality, getColorAverageBiomassInput } from 'utils/tools_functions';
import {
  CustomSimulationCell,
  CustomEditSelectCell,
} from '../../Datagrid/CustomSimulationDataGrid';
import bioLogo from 'assets/images/bio.png';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { StyledTooltip } from 'utils/Datagrid/CustomGenericDataGridUtils';
import { ORGANIC_STATES } from 'utils/GlobalVariables';
import { useTranslation } from 'react-i18next';

const DataGridParcelSynthesis = ({
  rows,
  columns,
  cultures,
  irrigationInformation,
  setHasChange,
  suggest,
  setSuggest,
  editedSynthesisCellsRef,
  isNitrogenASelectedConstraint,
}) => {
  const { t } = useTranslation();
  const [selectIndex, setSelectIndex] = useState(null);

  const renderSelectEditCell = (params) => {
    return (
      <CustomEditSelectCell
        params={params}
        culturesList={cultures}
        selectIndex={selectIndex}
        isNitrogenASelectedConstraint={isNitrogenASelectedConstraint}
      />
    );
  };

  const processedColumns = columns?.map((column, index) => {
    let processedColumn = {
      field: column.field,
      headerName: column.headerName,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      cellClassName: () => {
        if (index === 2) {
          return 'MuiDataGrid-colCellSynthesis MuiDataGrid-colCellSynthesis-parcelle';
        }
      },
    };

    if (column.field === 'parcel') {
      processedColumn = {
        ...processedColumn,
        cellClassName: 'MuiDataGrid-colCellSynthesis-parcelle',
        renderCell: (params) => {
          const row = rows?.find((row) => row.id === params.id);
          return (
            <span>
              {params.value} {row?.isParent && <span>*</span>}
            </span>
          );
        },
      };
    }

    if (index === 0 || index === 2) {
      processedColumn.minWidth = 170;
      processedColumn.maxWidth = 170;
    }
    if (index === 1) {
      processedColumn.minWidth = 110;
      processedColumn.maxWidth = 110;
      processedColumn.valueFormatter = (value) => {
        if (typeof value === 'number') {
          return Number(value.toFixed(2));
        }
        return value;
      };
    }

    if (index === 2) {
      processedColumn = {
        ...processedColumn,
        index: index - 3,
        renderCell: (params) => {
          return (
            <div className="cell-box">
              <div className="cell-main-content">
                {params.value}
                <div className="cultures-icons-wrapper">
                  <div className="culture-infos-icon">
                    <StyledTooltip
                      title={`${
                        params.row?.organicState === 'conversion'
                          ? t('simulation.result.common.bioConversion')
                          : params.row?.organicState === 'organic'
                            ? t('simulation.common.bioCulture')
                            : ''
                      }`}
                      placement="top"
                    >
                      {params.row?.organicState === 'conversion' ? (
                        <SyncAltIcon className="dropletLogo" />
                      ) : params.row?.organicState === 'organic' ? (
                        <img src={bioLogo} alt={t('alt.bioLogo')} className="dropletLogo" />
                      ) : null}
                    </StyledTooltip>
                  </div>
                </div>
              </div>
            </div>
          );
        },
      };
    }

    if (index >= 3) {
      const isBiomassColumn = column.field === 'avgDryMatterPerCampaign';

      processedColumn = {
        ...processedColumn,
        index: index - 3,
        editable: !isBiomassColumn,
        minWidth: 180,
        ...(!isBiomassColumn && {
          renderCell: (params) =>
            CustomSimulationCell(params, editedSynthesisCellsRef, setSelectIndex),
          renderEditCell: renderSelectEditCell,
        }),
      };

      if (isBiomassColumn) {
        processedColumn = {
          ...processedColumn,
          cellClassName: (params) => {
            if (params.field === 'avgDryMatterPerCampaign') {
              return getColorAverageBiomassInput(params.row.averageBiomassInput);
            }
            return '';
          },
        };
      }
    }

    return processedColumn;
  });

  const processedRows = rows?.map((row, index) => {
    let processedRow = {
      id: row?.id,
      organicState: row.organicState,
    };

    irrigationInformation?.map((year) => {
      if (year?.irrigationDistribution?.[index] > 0) {
        processedRow = {
          ...processedRow,
          isIrrigated: true,
        };
      }
    });

    processedColumns?.forEach((column, idx) => {
      const fieldName = column.field;

      switch (fieldName) {
        case 'parcel':
          processedRow[fieldName] = row?.name;
          break;
        case 'surface':
          processedRow[fieldName] = row?.surface;
          break;
        case 'avgDryMatterPerCampaign':
          const averageBiomassInput = row?.averageBiomassInput || 0;
          processedRow['averageBiomassInput'] = averageBiomassInput;
          processedRow[fieldName] = averageBiomassInput + ' ' + 't MS/ha';
          break;
        default:
          if (idx === 2) {
            processedRow[fieldName] = row?.previousCulture1;
          }
          if (idx > 2) {
            const cultureByIndex = row?.cultures[idx - 3];
            if (cultureByIndex && Array.isArray(cultureByIndex)) {
              const isParcelDivision = cultureByIndex.some((culture) => culture.isSplit);
              processedRow[fieldName] = {
                isParcelDivision,
                cultures: cultureByIndex ? cultureByIndex : [],
              };
            }
          }
      }
    });
    return processedRow;
  });

  const processRowUpdate = useCallback(
    (updatedRow, previousRow) => {
      if (!checkObjectsEquality(updatedRow, previousRow)) {
        const simulatedCultures = Object.entries(updatedRow).filter(
          ([_, value]) => typeof value === 'object',
        );

        const simulatedCulturesObject = Object.fromEntries(simulatedCultures);

        const simulatedCulturesArray = Object.entries(simulatedCulturesObject).map(
          (entry) => entry[1].cultures,
        );

        const flattenedCulturesArray = simulatedCulturesArray.flat();

        const editedCell = flattenedCulturesArray.find(
          (editedCulture) => editedCulture?.isEdited === true,
        );

        if (!editedSynthesisCellsRef.current.includes(editedCell?.cellId)) {
          editedSynthesisCellsRef.current.push(editedCell?.cellId);
        }

        const updatedSuggest = structuredClone(suggest);

        let simulatedCulturesArrayClone = structuredClone(simulatedCulturesArray);

        const foundParcel = updatedSuggest.parcels.find((parcel) => parcel.id === updatedRow.id);

        simulatedCulturesArrayClone.forEach((cultures) => {
          cultures.forEach((culture) => {
            if (culture.cellId) {
              delete culture.cellId;
            }
            if (culture.isEdited) {
              delete culture.isEdited;
            }
          });
        });

        foundParcel.cultures = simulatedCulturesArrayClone;

        setSuggest(updatedSuggest);
        setHasChange(true);

        return updatedRow;
      }

      return previousRow;
    },
    [rows],
  );

  const getRowClassName = () => 'Mui-even';
  return (
    <div className="datagrid_container">
      <CustomDataGridResult
        rows={processedRows}
        columns={processedColumns}
        getRowClassName={getRowClassName}
        processRowUpdate={processRowUpdate}
        theme={parcelSynthesisTheme}
        simulationContext={'parcel-synthesis-box'}
        fullSize={true}
        fromSimulation
      />
    </div>
  );
};

export default DataGridParcelSynthesis;
